import { styled } from '@mui/material';
import AssignmentSettings from 'components/ViewAssignment/AssignmentSettings';
import GradesAndFeedback from 'components/ViewAssignment/GradesAndFeedback';
import InstantStudentFeedback from 'components/ViewAssignment/InstantStudentFeedback';
import { Button, Container, Divider, Icon, Paper, RubricTable, Stack, Text, Tooltip, ViewMore } from 'shell/components';
import { useViewAccordionRubricTableButton } from 'shell/components/ViewRubricTable';
import { getTooltipComponent } from 'utils/helper';

const Mobile = ({
    assignment,
    submissions,
    onClickBack,
    onClickDuplicate,
    onClickUnpublish,
    onClickPublish,
    onClickEdit,
    onClickViewBaseline,
    onClickViewContentBank,
    onClickViewGradingSettings,
    onClickViewSubmissions,
    onClickUploadSubmissions,
    onSelect,
}) => {
    const { AccordionRubricTableButton, isExpanded } = useViewAccordionRubricTableButton();

    return (
        <Container device={'mobile'}>
            <CustomStack spacing={5}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Button startIcon={<Icon kind={'arrowLeft'} />} variant={'borderless'} onClick={onClickBack}>
                        Back
                    </Button>
                    <Stack direction={'row-reverse'} spacing={4}>
                        <Button startIcon={<Icon kind={'edit'} />} variant={'tertiary'} onClick={onClickEdit}>
                            Edit
                        </Button>
                        <Button
                            variant={'tertiary'}
                            onClick={
                                assignment.is_draft
                                    ? () => onClickPublish(assignment)
                                    : () => onClickUnpublish(assignment)
                            }
                        >
                            {assignment.is_draft ? 'Publish' : 'Unpublish'}
                        </Button>
                        <Button variant={'tertiary'} onClick={() => onClickDuplicate(assignment)}>
                            Duplicate
                        </Button>
                    </Stack>
                </Stack>
                <Stack spacing={2}>
                    <Text sx={{ fontStyle: 'italic' }} color={'light'}>
                        {assignment.is_draft ? 'Draft' : 'Published'}
                    </Text>
                    <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                        {assignment.title}
                    </Text>
                </Stack>
                <GradesAndFeedback
                    assignment={assignment}
                    submissions={submissions}
                    onClickViewSubmissions={onClickViewSubmissions}
                    onClickUploadSubmissions={onClickUploadSubmissions}
                    onSelect={onSelect}
                />
                <InstantStudentFeedback assignment={assignment} />
                <CustomPaper elevation={0}>
                    <Stack spacing={5}>
                        <Stack spacing={2}>
                            <Text>Assignment Description</Text>
                            {assignment.description ? (
                                <ViewMore color={'light'} rich text={assignment.description} />
                            ) : (
                                <Text color={'light'}>None</Text>
                            )}
                        </Stack>
                        <Stack spacing={2}>
                            <Stack justifyContent='space-between' direction='row'>
                                <Text>Assignment Rubric</Text>
                                {assignment.rubric && <AccordionRubricTableButton />}
                            </Stack>
                            {assignment.rubric ? (
                                <RubricTable type={'view'} isExpanded={isExpanded} data={assignment.rubric.criteria} />
                            ) : (
                                <Text color={'light'}>None</Text>
                            )}
                        </Stack>
                        {assignment.baseline && (
                            <Stack divider={<Divider />} spacing={2}>
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text>Baseline</Text>
                                    <Tooltip
                                        content={getTooltipComponent(
                                            'Assignment Baseline',
                                            'For optimal AI feedback and grading outcomes, we recommend uploading and grading a previous assignment. This will help align the AI with your assessment practices.'
                                        )}
                                        type={'info'}
                                    />
                                </Stack>
                                <Button
                                    sx={{ alignSelf: 'flex-start' }}
                                    variant={'borderless'}
                                    onClick={onClickViewBaseline}
                                >
                                    View Assignment Baseline
                                </Button>
                            </Stack>
                        )}
                        <Stack divider={<Divider />} spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text>Content Bank</Text>
                                <Tooltip
                                    content={
                                        'Files added to the Content Bank allow our AI to retrieve relevant context from these documents, enabling it to generate more personalized and accurate feedback and grading suggestions.'
                                    }
                                    type={'info'}
                                />
                            </Stack>
                            {assignment.content_bank_resources?.filter(({ active }) => active)?.length ? (
                                <Button
                                    sx={{ alignSelf: 'flex-start' }}
                                    variant={'borderless'}
                                    onClick={onClickViewContentBank}
                                >
                                    View Content Bank
                                </Button>
                            ) : (
                                <Text color={'light'}>None</Text>
                            )}
                        </Stack>
                        <AssignmentSettings
                            assignment={assignment}
                            onClickViewGradingSettings={onClickViewGradingSettings}
                        />
                    </Stack>
                </CustomPaper>
            </CustomStack>
        </Container>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        paddingBottom: theme.spacing(5),
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        borderRadius: '12px',
        padding: theme.spacing(5),
    };
});

export default Mobile;
